import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Table, Tag, Divider, Button, Statistic } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Title, Text, Paragraph } = Typography;

const ReportContainer = styled.div`
  padding: 24px;
  background-color: #f0f2f5;
`;

const DetailItem = ({ title, children, span = 8 }) => (
  <Col span={span} style={{ marginBottom: '16px' }}>
    <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>{title}</Text>
    <Text strong>{children}</Text>
  </Col>
);

const formatTimestamp = (unixTimestamp) => {
  if (!unixTimestamp) return 'N/A';
  // Corrected to use the year from the timestamp data
  return new Date(unixTimestamp * 1000).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const statusToTag = (value) => {
  if (typeof value === 'boolean') {
    return value ? <Tag color="success">Yes</Tag> : <Tag color="error">No</Tag>;
  }
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();
    if (lowerValue.includes('success') || lowerValue.includes('running') || lowerValue.includes('registered') || lowerValue.includes('approved')) {
      return <Tag color="success">{value}</Tag>;
    }
    if (lowerValue.includes('fail') || lowerValue.includes('error')) {
      return <Tag color="error">{value}</Tag>;
    }
  }
  return value;
};


const TroubleshootReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reportData = location.state?.reportData;

  if (!reportData) {
    return (
      <ReportContainer style={{ textAlign: 'center' }}>
        <Title level={2}>No Report Data</Title>
        <Text>Could not find any data to display. Please try generating a report again.</Text>
        <br />
        <Button onClick={() => navigate(-1)} style={{marginTop: '20px'}}>Go Back</Button>
      </ReportContainer>
    );
  }

  const { summary, detailed_analysis } = reportData;

  // --- UPDATED: Columns for the main table row ---
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (ts) => formatTimestamp(ts),
    },
    {
      title: 'Request Type',
      dataIndex: 'request_type',
      key: 'request_type',
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Hostname',
      dataIndex: 'hostname',
      key: 'hostname',
    },
    {
      title: 'Policy Action',
      dataIndex: 'policy_action',
      key: 'policy_action',
      render: (action) => statusToTag(action),
    },
  ];

  // --- UPDATED: The expanded view now contains ALL other details from the original card layout ---
  const expandedRowRender = (record) => {
    return (
        <div style={{ padding: '16px', backgroundColor: 'var(--an-bg)', border: '1px solid #e8e8e8', borderRadius: '4px' }}>
            <Title level={5}>Status Details</Title>
            <Row gutter={[16, 16]}>
                <DetailItem title="Service Status" span={8}>{statusToTag(record.service_status)}</DetailItem>
                <DetailItem title="User Registered" span={8}>{statusToTag(record.user_registered)}</DetailItem>
                <DetailItem title="Credentials Status" span={8}>{statusToTag(record.credentials_status)}</DetailItem>
            </Row>
            <Divider />
            <Title level={5}>Policy Analysis</Title>
            <Row gutter={[16, 16]}>
                <DetailItem title="Policy Found" span={8}>{statusToTag(record.policy_analysis.policy_found)}</DetailItem>
                <DetailItem title="Policy Name" span={8}>{record.policy_analysis.policy_name}</DetailItem>
                <DetailItem title="Policy Type" span={8}>{record.policy_analysis.policy_type}</DetailItem>
                <DetailItem title="Policy Status" span={8}>{statusToTag(record.policy_analysis.policy_status)}</DetailItem>
                <DetailItem title="Conditional Access" span={8}>{statusToTag(record.policy_analysis.is_conditional_access)}</DetailItem>
            </Row>
             <Divider />
            <Title level={5}>Result</Title>
            <Row>
                <DetailItem title="Failure Reason" span={24}>{statusToTag(record.failure_reason)}</DetailItem>
            </Row>
        </div>
    );
  };

  return (
    <ReportContainer>
      <Button 
        type="primary" 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate(-1)} 
        style={{ marginBottom: '24px' }}
      >
        Back
      </Button>
      
      <Title level={2} style={{ marginBottom: '24px' }}>
        Authentication Troubleshooting Report for <Text code style={{ fontSize: '24px' }}>{summary.user_email}</Text>
      </Title>

      {/* Compact Summary Card (Unchanged) */}
      <Card title="Executive Summary" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          <Col span={6}><Statistic title="Logs Analyzed" value={summary.logs_analyzed} /></Col>
          <Col span={6}><Statistic title="Success" value={summary.successful_authentications} valueStyle={{ color: '#3f8600' }} /></Col>
          <Col span={6}><Statistic title="Failures" value={summary.failed_authentications} valueStyle={{ color: '#cf1322' }} /></Col>
          <Col span={6}>
            <Title level={5} style={{color: 'rgba(0, 0, 0, 0.45)', fontWeight: 'normal'}}>MFA (Success / Fail)</Title>
            <Paragraph style={{margin: 0}}>
                <Tag color="success">{summary.mfa_analysis?.mfa_success}</Tag> / <Tag color="error">{summary.mfa_analysis?.mfa_failed}</Tag>
            </Paragraph>
          </Col>
        </Row>
      </Card>

      <Title level={3} style={{ marginBottom: '16px' }}>Detailed Analysis</Title>
      <Card>
        <Table
            columns={columns}
            dataSource={detailed_analysis.map(log => ({ ...log, key: log.log_number }))}
            expandable={{
                expandedRowRender,
                expandIcon: ({ expanded, onExpand, record }) =>
                    expanded ? (
                        <MinusOutlined onClick={e => onExpand(record, e)} style={{ cursor: 'pointer' }} />
                    ) : (
                        <PlusOutlined onClick={e => onExpand(record, e)} style={{ cursor: 'pointer' }} />
                    ),
            }}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      </Card>
    </ReportContainer>
  );
};

export default TroubleshootReport;